import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ShieldCheck, Upload, Save } from "lucide-react";
import BackButton from "@/components/BackButton";

const ORG_ROLES = [
  "ORG_ADMIN", "PROCUREMENT_MANAGER", "PURCHASE_EXECUTIVE",
  "FINANCE_MANAGER", "ACCOUNTS_EXECUTIVE",
  "INVENTORY_MANAGER", "STORE_MANAGER", "WAREHOUSE_MANAGER",
  "PRODUCTION_MANAGER", "QC_MANAGER",
  "SALES_MANAGER", "SALES_MARKETING_MANAGER",
  "DISPATCH_MANAGER", "DISPATCH_EXECUTIVE",
];

const RESOURCE_META = {
  items: { label: "Item / Purchase master", desc: "Raw materials, packing, consumables (Purchase → Items)" },
  finished_products: { label: "Finished products", desc: "SKUs used across Sales, Production, QC, Dispatch" },
};

export default function BulkUploadPermissions() {
  const [perms, setPerms] = useState({});
  const [resources, setResources] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/org/bulk-upload/permissions");
      setPerms(data.permissions || {});
      setResources(data.resources || []);
      setLoaded(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = (resource, role) => {
    setPerms((p) => {
      const cur = new Set(p[resource] || []);
      cur.has(role) ? cur.delete(role) : cur.add(role);
      return { ...p, [resource]: Array.from(cur) };
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.put("/org/bulk-upload/permissions", { permissions: perms });
      setPerms(data.permissions);
      toast.success("Permissions updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="p-4 sm:p-6 lg:p-8"><div className="surface-card p-8 text-center text-muted-foreground">Loading permissions…</div></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="bulk-upload-perms-page">
      <BackButton />
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 border border-orange-200 text-[10px] font-bold uppercase tracking-[0.25em] text-orange-700">
          <ShieldCheck className="w-3 h-3" /> Org Admin only
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-black flex items-center gap-2 mt-3"><Upload className="w-7 h-7" /> Bulk upload permissions</h1>
        <p className="text-muted-foreground max-w-2xl">Choose which roles are allowed to upload data in bulk. Org Admin always has permission by default.</p>
      </div>

      {resources.map((res) => (
        <div key={res} className="surface-card p-4 sm:p-5 space-y-3" data-testid={`bulk-perm-${res}`}>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display font-black text-lg">{RESOURCE_META[res]?.label || res}</h3>
              <Badge variant="outline" className="font-mono text-[10px]">{res}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{RESOURCE_META[res]?.desc}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {ORG_ROLES.map((role) => {
              const enabled = (perms[res] || []).includes(role);
              const isOrgAdmin = role === "ORG_ADMIN";
              return (
                <div key={role} className={`flex items-center justify-between p-3 rounded-lg border ${enabled ? "border-orange-200 bg-orange-50/40" : "border-border"}`}>
                  <div>
                    <div className="text-sm font-semibold">{role.replace(/_/g, " ")}</div>
                    {isOrgAdmin ? <div className="text-[10px] text-muted-foreground">Always allowed</div> : null}
                  </div>
                  <Switch
                    checked={enabled || isOrgAdmin}
                    disabled={isOrgAdmin}
                    onCheckedChange={() => toggle(res, role)}
                    data-testid={`bulk-perm-${res}-${role}`}
                  />
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex justify-end sticky bottom-4">
        <Button onClick={save} disabled={saving} className="shadow-lg" data-testid="bulk-perm-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save permissions"}
        </Button>
      </div>
    </div>
  );
}
